import React, { useEffect, useRef, useState } from 'react'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import {
  ClusterShipperThirdPartyConversionDeliveryDto,
  NeedsClusterReinstalledClusterWithOdometerPhotoDto,
  NeedsClusterReinstalledComboPhotoDto,
  NeedsClusterReinstalledImporterDoorLabelPhotoDto,
  NeedsClusterRemovedPulledClusterPhotoDto,
  VehicleCheckInCheckInClusterPhotoDto,
  VehicleOdometerAfterModificationPhotoDto
} from 'dtos'

interface ClusterPhotosViewDialogProps {
  photoType:
    | 'check-in-cluster'
    | 'pulled-cluster'
    | 'third-party-conversion-delivery'
    | 'cluster-with-odometer'
    | 'importer-door-label'
    | 'combo'
    | 'odometer-after-modification'
  open: boolean
  onClose: () => void
  vehicleGuid: string
  clusterShipperGuid?: string
  photoTypes: Array<
    | 'check-in-cluster'
    | 'pulled-cluster'
    | 'third-party-conversion-delivery'
    | 'cluster-with-odometer'
    | 'importer-door-label'
    | 'combo'
    | 'odometer-after-modification'
  >
}

export default function ClusterPhotosViewDialog({
  photoType,
  open,
  onClose,
  vehicleGuid,
  clusterShipperGuid,
  photoTypes
}: ClusterPhotosViewDialogProps) {
  const [photoSrc, setPhotoSrc] = useState<string>('')
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [rotation, setRotation] = useState<number>(0)
  const [zoom, setZoom] = useState<number>(1)

  const rotateLeft = () => setRotation(prev => prev - 90)
  const rotateRight = () => setRotation(prev => prev + 90)
  const zoomIn = () => setZoom(prev => prev + 0.2)
  const zoomOut = () => setZoom(prev => Math.max(1, prev - 0.2))

  const [isDragging, setIsDragging] = useState(false)
  const [originX, setOriginX] = useState(0)
  const [originY, setOriginY] = useState(0)
  const [translateX, setTranslateX] = useState(0)
  const [translateY, setTranslateY] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const handleMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    if (zoom > 1) {
      e.preventDefault()
      setIsDragging(true)
      setOriginX(e.clientX)
      setOriginY(e.clientY)
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (isDragging) {
      const deltaX = e.clientX - originX
      const deltaY = e.clientY - originY
      let newTranslateX = translateX + deltaX
      let newTranslateY = translateY + deltaY

      if (containerRef.current && imageRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()

        const imageWidth = imageRef.current.offsetWidth * zoom
        const imageHeight = imageRef.current.offsetHeight * zoom

        const maxTranslateX = (imageWidth - containerRect.width) / 2
        const maxTranslateY = (imageHeight - containerRect.height) / 2

        const padding = 100

        newTranslateX = Math.max(-maxTranslateX - padding, Math.min(newTranslateX, maxTranslateX + padding))
        newTranslateY = Math.max(-maxTranslateY - padding, Math.min(newTranslateY, maxTranslateY + padding))

        setTranslateX(newTranslateX)
        setTranslateY(newTranslateY)
      }

      setTranslateX(newTranslateX)
      setTranslateY(newTranslateY)
      setOriginX(e.clientX)
      setOriginY(e.clientY)
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    if (open) {
      const index = photoTypes.indexOf(photoType)
      setCurrentPhotoIndex(index !== -1 ? index : 0)
    }
  }, [open, photoType, photoTypes])

  useEffect(() => {
    if (open && vehicleGuid) {
      fetchPhoto(photoTypes[currentPhotoIndex])
      console.log('fetchPhoto', currentPhotoIndex)
    } else {
      setPhotoSrc('')
    }
  }, [open, vehicleGuid, currentPhotoIndex, photoTypes])

  const fetchPhoto = async (
    type:
      | 'check-in-cluster'
      | 'pulled-cluster'
      | 'third-party-conversion-delivery'
      | 'cluster-with-odometer'
      | 'importer-door-label'
      | 'combo'
      | 'odometer-after-modification'
  ) => {
    setIsLoading(true)
    let fileName: string = ''

    try {
      if (type === 'check-in-cluster') {
        const response = await controllers.getVehicleCheckInCheckInClusterPhotoInformation(vehicleGuid)
        const checkInClusterPhoto: VehicleCheckInCheckInClusterPhotoDto = response.value
        const checkInDocumentGuid = checkInClusterPhoto.documentGuid ?? ''

        if (checkInDocumentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, checkInDocumentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'pulled-cluster') {
        const response = await controllers.getNeedsClusterRemovedPulledClusterPhotoInformation(vehicleGuid)
        const pulledClusterPhoto: NeedsClusterRemovedPulledClusterPhotoDto = response.value
        const pulledDocumentGuid = pulledClusterPhoto.documentGuid ?? ''

        if (pulledDocumentGuid) {
          const base64 = await controllers.getNeedsClusterRemovedPhotoBase64(vehicleGuid, pulledDocumentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'third-party-conversion-delivery') {
        if (!clusterShipperGuid) {
          setPhotoSrc('')
          return
        }
        const response = await controllers.getClusterShippersThirdPartyConversionDeliveryInformation(clusterShipperGuid)
        const thirdPartyConversionDeliveryPhoto: ClusterShipperThirdPartyConversionDeliveryDto = response.value
        fileName = thirdPartyConversionDeliveryPhoto.clusterShipperThirdPartyConversionDeliveryPhotoFileName ?? ''

        if (fileName) {
          const base64 = await controllers.getClusterShipperThirdPartyConversionDeliveryPhotoBase64(
            clusterShipperGuid,
            fileName
          )
          setPhotoSrc(`data:image/${fileName.split('.').pop()};base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'cluster-with-odometer') {
        const response = await controllers.getNeedsClusterReinstalledClusterWithOdometerLabelPhotoInformation(vehicleGuid)
        const clusterWithOdometerPhoto: NeedsClusterReinstalledClusterWithOdometerPhotoDto = response.value
        const speedometerAfterDocumentGuid = clusterWithOdometerPhoto.documentGuid ?? ''

        if (speedometerAfterDocumentGuid) {
          const base64 = await controllers.getNeedsClusterReinstalledPhotoBase64(vehicleGuid, speedometerAfterDocumentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'importer-door-label') {
        const response = await controllers.getNeedsClusterReinstalledImporterDoorLabelPhotoInformation(vehicleGuid)
        const importerDoorLabelPhoto: NeedsClusterReinstalledImporterDoorLabelPhotoDto = response.value
        const importerDocumentGuid = importerDoorLabelPhoto.documentGuid ?? ''

        if (importerDocumentGuid) {
          const base64 = await controllers.getNeedsClusterReinstalledPhotoBase64(vehicleGuid, importerDocumentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'combo') {
        const response = await controllers.getNeedsClusterReinstalledComboLabelPhotoInformation(vehicleGuid)
        const comboPhoto: NeedsClusterReinstalledComboPhotoDto = response.value
        const comboDocumentGuid = comboPhoto.documentGuid ?? ''

        if (comboDocumentGuid) {
          const base64 = await controllers.getNeedsClusterReinstalledPhotoBase64(vehicleGuid, comboDocumentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'odometer-after-modification') {
        const response = await controllers.getVehicleOdometerAfterModificationPhoto(vehicleGuid)
        const photo: VehicleOdometerAfterModificationPhotoDto = response.value
        fileName = photo.displayFileName ?? ''

        if (photo.documentGuid) {
          const base64 = await controllers.getNeedsClusterReinstalledPhotoBase64(vehicleGuid, photo.documentGuid)
          setPhotoSrc(`data:image/${fileName.split('.').pop()};base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      }
    } catch (error) {
      console.error('Error fetching photo:', error)
      console.log('fetchPhoto', type)
      setPhotoSrc('')
    } finally {
      setIsLoading(false)
    }
  }

  const goToPreviousPhoto = () => {
    setCurrentPhotoIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : photoTypes.length - 1))
    console.log(currentPhotoIndex)
  }

  const goToNextPhoto = () => {
    setCurrentPhotoIndex(prevIndex => {
      return prevIndex < photoTypes.length - 1 ? prevIndex + 1 : 0
    })
    console.log(currentPhotoIndex)
  }

  useEffect(() => {
    setRotation(0)
    setZoom(1)
    setTranslateX(0)
    setTranslateY(0)
  }, [photoSrc])

  const getTitle = () => {
    switch (photoTypes[currentPhotoIndex]) {
      case 'check-in-cluster':
        return 'Speedometer Before Modification Photo'
      case 'pulled-cluster':
        return 'Pulled Cluster Photo'
      case 'third-party-conversion-delivery':
        return 'Third Party Conversion Delivery Photo'
      case 'cluster-with-odometer':
        return 'Speedometer After Modification Photo'
      case 'importer-door-label':
        return 'Importer Door Label Photo'
      case 'combo':
        return 'Combo Photo'
      case 'odometer-after-modification':
        return 'Odometer After Modification Photo'
      default:
        return 'View Check In Photo'
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogContent>
        <Grid container spacing={2} alignItems='center'>
          <DialogTitle>{getTitle()}</DialogTitle>

          <Grid item xs={12}>
            {isLoading ? (
              <Grid container justifyContent='center'>
                <CircularProgress />
              </Grid>
            ) : photoSrc ? (
              <div
                ref={containerRef}
                style={{
                  overflow: 'hidden',
                  width: '100%',
                  height: '60vh',
                  backgroundColor: 'black',
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px',
                  borderRadius: '8px'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <Button
                  style={{ position: 'absolute', top: '50%', left: '10px', transform: 'translateY(-50%)', zIndex: 10 }}
                  onClick={goToPreviousPhoto}
                  variant='text'
                >
                  <KeyboardArrowLeft style={{ fontSize: 40 }} />
                </Button>
                <Button
                  style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', zIndex: 10 }}
                  onClick={goToNextPhoto}
                  variant='text'
                >
                  <KeyboardArrowRight style={{ fontSize: 40 }} />
                </Button>
                <img
                  ref={imageRef}
                  src={photoSrc}
                  alt={getTitle()}
                  style={{
                    width: '80%',
                    height: '100%',
                    transform: `translate(${translateX}px, ${translateY}px) rotate(${rotation}deg) scale(${zoom})`,
                    transformOrigin: 'center center',
                    cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    transition: isDragging ? 'none' : 'transform 0.3s ease'
                  }}
                  draggable={false}
                />
              </div>
            ) : (
              <Grid container justifyContent='center'>
                <Typography variant='body1'>No Photo Available</Typography>
              </Grid>
            )}

            <Grid container justifyContent='space-between' alignItems='center'>
              <Grid item ml={1.25}>
                <Button endIcon={<ZoomInIcon />} color='primary' onClick={zoomIn} variant='text'>
                  Zoom In
                </Button>
              </Grid>

              <Grid item mr={1.25}>
                <Button endIcon={<ZoomOutIcon />} color='primary' onClick={zoomOut} variant='text'>
                  Zoom Out
                </Button>
              </Grid>
            </Grid>

            <Grid container xs={12} margin='auto' mt={1} justifyContent='center' alignItems='center'>
              <Grid item xs={4} sm='auto'>
                <Button startIcon={<RotateLeftIcon />} color='primary' onClick={rotateLeft} variant='text'>
                  ROTATE LEFT
                </Button>
              </Grid>

              <Grid item xs={4}></Grid>

              <Grid item xs={4} sm='auto'>
                <Button endIcon={<RotateRightIcon />} color='primary' onClick={rotateRight} variant='text'>
                  ROTATE RIGHT
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <GreenText>
            {`
Clicking the Pulled Cluster image will select it.`}
          </GreenText>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color='inherit' onClick={onClose} variant='text'>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  )
}
